import React, { MutableRefObject, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { useModal } from 'hooks/useModal';
import { useDebounce } from 'hooks/useDebounce';
import { useQuery } from '@apollo/client';
import { FormProvider, useWatch } from 'react-hook-form';
import { ChargeBucketRef } from 'modules/equipment/types';
import { useEquipmentAssets } from 'modules/equipment/api';
import { Equipment, useEquipmentActions } from 'modules/equipment';
import { Listing, useListing } from 'components/shared/TableListing';
import { QuoteCalcPreviewInput } from '@trnsact/trnsact-shared-types';
import { ListingForm, Group } from 'components/shared/TableListing/types';
import { GraphqlResolverInput, ModalsKeys, Nullable, QueryResponse } from 'global';

import { quoteAPI } from '../../../api';
import { GroupSubtotalRow } from './cells';
import { ChargeListingService } from '../../../lib';
import { DealerProfitability } from './DealerProfitability';
import { quoteMapper, quoteCalcMapper } from '../../../mappers';
import { GroupHeader, groupHeaderCellStyles } from './GroupHeader';
import { QuoteCalcNameSpace, QuoteCalcViewModel } from '../../../api/types';
import { invoiceDetailsListingColumns } from './invoiceDetailsListingConfig';
import { Quote, InvoiceListingActions, QuoteChargeListingEntity, QuoteChargeListingContext } from '../../../types';

interface Props {
  VOId: string;
  dynamicsVOId: string;
  quote: Nullable<Quote>;
  equipmentList: Equipment[];
  showDealerProfitability?: boolean;
  onSubscribe: (fields: QuoteChargeListingEntity[]) => void;
  actionPanelBottom?: (actions: InvoiceListingActions) => React.ReactNode;
  chargesSubmitRef: MutableRefObject<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>;
}

export const InvoiceDetailsListing = ({
  VOId,
  quote,
  onSubscribe,
  dynamicsVOId,
  chargesSubmitRef,
  equipmentList = [],
  actionPanelBottom,
  showDealerProfitability = true,
}: Props) => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const isMount = useRef<boolean>(false);
  const chargeService = useMemo(() => new ChargeListingService(), []);

  const { onDeleteEquipment, onUpdateEquipment, onCreateEquipment, isAnyActionInProgress } = useEquipmentActions();
  const { handleUploadImages } = useEquipmentAssets();

  const { handleOpen: openCustomChargeDialog } = useModal(ModalsKeys.CreateChargesEquipmentDialog);
  const { handleOpen: handleOpenChargesDialog, handleClose } = useModal(ModalsKeys.ChargesEquipmentDialog);
  const { handleOpen: handleOpenEquipmentDialog, handleClose: handleCloseEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );

  const initialFields = useMemo(
    () => quoteMapper.toListingRows(VOId, equipmentList, quote),
    [VOId, equipmentList, quote]
  );

  const { form, columns, onRemove, tableData, handleInsertRow, handleRemoveRow, handleUpdateRow } = useListing<
    QuoteChargeListingEntity,
    QuoteChargeListingContext
  >({ headerColumns: invoiceDetailsListingColumns, bodyFields: initialFields }, { shouldUnregister: false });

  useImperativeHandle(
    chargesSubmitRef,
    () => ({
      submit: () => form.getValues(),
    }),
    [form]
  );

  const watchedRows = useWatch({ control: form.control, name: 'bodyFields' });

  const inputSignature = useMemo(() => quoteCalcMapper.toInputSignature(watchedRows ?? []), [watchedRows]);
  const debouncedSignature = useDebounce(inputSignature, 400);
  const previewRows = useMemo(
    () => quoteCalcMapper.toPreviewRows(form.getValues('bodyFields') ?? []),
    // eslint-disable-next-line
    [debouncedSignature]
  );

  const lastSavedSignatureRef = useRef<string>(inputSignature);

  const syncCalcIntoForm = useCallback(
    (calc: Nullable<QuoteCalcViewModel>, isMounted: boolean = false) => {
      if (!calc?.rows?.length) return;

      const currentRows = form.getValues('bodyFields');

      const fieldUpdates = quoteCalcMapper.toFieldUpdates(currentRows, calc);

      fieldUpdates.forEach(({ path, value }) => {
        form.setValue(path, value, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
      });

      // When isMounted === false code skip lastSavedSignatureRef condition. Reason - call sub first time without manual updates
      if (isMounted && lastSavedSignatureRef.current === debouncedSignature) return;

      lastSavedSignatureRef.current = debouncedSignature;
      onSubscribe(form.getValues('bodyFields'));
    },
    [form, onSubscribe, debouncedSignature]
  );

  const { data, previousData, loading } = useQuery<
    QuoteCalcNameSpace<QueryResponse<QuoteCalcViewModel>>,
    GraphqlResolverInput<QuoteCalcPreviewInput>
  >(quoteAPI.getQuoteCalcPreview, {
    skip: !previewRows.length,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        rows: previewRows,
      },
    },
    onCompleted: response => {
      const calc = response?.quoteCalc?.result?.entity ?? null;

      if (!isMount.current) {
        syncCalcIntoForm(calc);
        isMount.current = true;
      } else {
        syncCalcIntoForm(calc, isMount.current);
      }
    },
  });

  const serverCalc = (data ?? previousData)?.quoteCalc?.result?.entity ?? null;

  const handleAddCharge = useCallback(
    (bucket: ChargeBucketRef) => {
      const equipmentCharges = quoteMapper.getChargesFromEquipment(bucket, equipmentList);

      const bodyFieldsValue = form.getValues('bodyFields');

      const resolveInsertIndex = () => chargeService.getInsertIndex(bodyFieldsValue ?? [], bucket);

      handleOpenChargesDialog({
        ...(equipmentCharges ? { externalCharges: equipmentCharges } : {}),
        onOpenCreateChargeDialog: () => {
          openCustomChargeDialog({
            onConfirm: newCharge => {
              const newRow = chargeService.createCustomChargeRow(bucket, newCharge.name);

              handleInsertRow(newRow, resolveInsertIndex(), { shouldFocus: false });
              handleClose();
            },
          });
        },
        onConfirm: charges => {
          handleClose();
          let insertAt = resolveInsertIndex();

          charges.forEach(template => {
            const newRow = chargeService.createChargeRowFromTemplate(bucket, template);

            handleInsertRow(newRow, insertAt, { shouldFocus: false });
            insertAt += 1;
          });
        },
      });
    },
    [equipmentList, form, handleOpenChargesDialog, chargeService, openCustomChargeDialog, handleInsertRow, handleClose]
  );

  const handleAddEquipment = () => {
    handleOpenEquipmentDialog({
      equipmentId: '',
      onCancel: handleCloseEquipmentDialog,
      onImagesSaved: setSelectedImages,
      onSubmit: async ({ createDTO }) => {
        handleCloseEquipmentDialog();

        const equipmentId = await onCreateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...createDTO,
        });

        // Upload images if any were selected
        if (selectedImages.length > 0 && equipmentId) {
          await handleUploadImages(equipmentId, 'IMAGE', selectedImages);
          setSelectedImages([]); // Clear after upload
        }

        handleInsertRow(quoteMapper.toListingRow({ equipmentId, ...createDTO }, quote), 0, { shouldFocus: false });
      },
    });
  };

  const handleEditEquipment = (equipmentId: string, rowIndex: number) => {
    handleOpenEquipmentDialog({
      equipmentId,
      onCancel: handleCloseEquipmentDialog,
      onSubmit: async ({ updateDTO }) => {
        handleCloseEquipmentDialog();

        await onUpdateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...updateDTO,
        });

        handleUpdateRow(quoteMapper.toListingRow(updateDTO, quote), rowIndex);
      },
    });
  };

  const handleRemoveEquipment = (equipmentId: string, rowIndex: number) => {
    onDeleteEquipment(
      {
        equipmentId,
        vendorOpportunityId: VOId,
        dynamicsVendorOpportunityId: dynamicsVOId,
      },
      () => {
        onRemove(rowIndex);
      }
    );
  };

  const groups = useMemo<Group<QuoteChargeListingEntity>[]>(() => {
    if (!tableData.length) return [];

    return chargeService.buildBuckets(tableData, VOId).map(bucket => ({
      name: bucket.id,
      data: bucket.data,
      beforeGroup: {
        cellStyles: groupHeaderCellStyles,
        render: () => <GroupHeader title={bucket.title} onAdd={() => handleAddCharge(bucket)} />,
      },
      afterGroup: {
        disableCellWrap: true,
        render: () => <GroupSubtotalRow data={bucket.data} columns={columns} priceCalc={serverCalc} />,
      },
    }));
  }, [VOId, tableData, chargeService, handleAddCharge, columns, serverCalc]);

  return (
    <FormProvider {...form}>
      <Stack spacing={2}>
        <Listing<QuoteChargeListingEntity, QuoteChargeListingContext>
          groups={groups}
          loading={loading}
          columns={columns}
          tableData={tableData}
          injectContext={{
            form,
            chargeService,
            invoiceId: VOId,
            priceCalc: serverCalc,
            onRemoveRow: handleRemoveRow,
            onEditEquipment: handleEditEquipment,
            onRemoveEquipment: handleRemoveEquipment,
            isAnyEquipmentActionInProgress: isAnyActionInProgress,
          }}
          actionPanelBottom={() => actionPanelBottom?.({ onAddEquipment: handleAddEquipment })}
        />

        {showDealerProfitability && <DealerProfitability />}
      </Stack>
    </FormProvider>
  );
};
